import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0, User as Auth0User } from '@auth0/auth0-react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Snackbar, Alert, CircularProgress, Typography, Button } from '@mui/material';
import { Box } from '@mui/material';
import { procogiaMuiTheme } from './theme/procogia';
import { clearAuth0State } from './utils/clearAuth0State';

// Import components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import AuthCallback from './components/AuthCallback';
import ProfileSelection from './components/ProfileSelection';
import SimpleAIChat from './pages/SimpleAIChat';  // New simple chat component
import ModernDashboard from './pages/ModernDashboard';
import Analytics from './pages/Analytics';
import AdminPanel from './pages/AdminPanel';
import LoginPage from './pages/LoginPage';
import ErrorBoundary from './components/ErrorBoundary';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
  avatar?: string;
}

interface SystemStatus {
  database: boolean;
  mcpServers: boolean;  
  aiAgent: boolean;
  llmStatus: boolean;
}

const App: React.FC = () => {
  console.log('🎨 App component rendering/re-rendering');
  
  const { isLoading, isAuthenticated, user: auth0User, getAccessTokenSilently, error } = useAuth0();
  const [authError, setAuthError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [showProfileSelection, setShowProfileSelection] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    database: true,
    mcpServers: true,
    aiAgent: true,
    llmStatus: true,
  });

  console.log('👤 Current user state:', { user, isAuthenticated, hasAuth0User: !!auth0User });

  // Auto-detect and fix Auth0 stuck state
  useEffect(() => {
    const isRootPage = window.location.pathname === '/';
    const isCallbackPage = window.location.pathname === '/callback';
    const hasAuth0Cache = localStorage.getItem('@@auth0spajs@@::m9mqRYRXS75BOybSgDqW7RUCtsd6evqn::@@user@@');

    // Only clear cache if we're on root page, have cache, not loading, not authenticated, 
    // AND not coming from callback (to avoid interfering with auth flow)
    if (isRootPage && hasAuth0Cache && !isLoading && !isAuthenticated && !isCallbackPage) {
      // Additional check: only clear if we've been on this page for a bit (avoid immediate clearing)
      const clearTimer = setTimeout(() => {
        console.log('🔧 Detected Auth0 cache issue after delay, clearing state...');
        clearAuth0State();
        // Small delay to ensure cleanup, then reload
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }, 2000); // Wait 2 seconds before clearing

      return () => clearTimeout(clearTimer);
    }
  }, [isLoading, isAuthenticated]);

  // Handle Auth0 errors (but ignore "Invalid state" when user is authenticated)
  useEffect(() => {
    console.log('🐛 DEBUG: Auth0 error state changed -', { 
      hasError: !!error,
      errorMessage: error?.message,
      errorName: error?.name,
      isLoading,
      isAuthenticated,
      timestamp: new Date().toISOString()
    });
    
    if (error) {
      // Don't treat "Invalid state" as a real error if user is authenticated
      const isInvalidStateError = error.message && error.message.toLowerCase().includes('invalid state');
      const shouldIgnoreError = isInvalidStateError && isAuthenticated;
      
      if (shouldIgnoreError) {
        console.log('✅ DEBUG: Ignoring "Invalid state" error - user is authenticated successfully');
        setAuthError(null); // Clear any previous error state
      } else {
        console.error('🚨 Auth0 error details:', {
          error,
          message: error.message,
          name: error.name,
          stack: error.stack
        });
        setAuthError(error.message);

        // If it's a domain-related error, clear Auth0 state
        if (error.message.includes('login.procogia.ai') || error.message.includes('redirect_uri')) {
          console.log('🔧 Detected domain-related Auth0 error, clearing state...');
          clearAuth0State();
        }
      }
    } else {
      console.log('✅ DEBUG: No Auth0 errors detected');
      setAuthError(null); // Clear error state when no error
    }
  }, [error, isLoading, isAuthenticated]);

  // Convert Auth0 user to app user when authenticated
  useEffect(() => {
    console.log('🐛 DEBUG: Auth state change detailed -', { 
      isAuthenticated, 
      hasAuth0User: !!auth0User, 
      hasAppUser: !!user,
      auth0UserEmail: auth0User?.email,
      appUserEmail: user?.email,
      isLoading,
      hasError: !!error,
      errorMessage: error?.message,
      timestamp: new Date().toISOString()
    });

    if (isAuthenticated && auth0User && !user) {
      console.log('🔐 Auth0 user authenticated:', auth0User.email);
      
      // Check if user has previously selected a profile
      const savedUser = localStorage.getItem('telecom_demo_user');
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          // Update with fresh Auth0 data but keep the role
          const appUser: User = {
            id: auth0User.sub || 'auth0-user',
            name: auth0User.name || parsedUser.name,
            email: auth0User.email || parsedUser.email,
            role: parsedUser.role || 'customer',
            avatar: auth0User.picture || parsedUser.avatar
          };
          setUser(appUser);
          console.log('✅ Restored user profile with role:', appUser.role);
        } catch (e) {
          console.log('🎯 No valid saved profile, showing profile selection');
          setShowProfileSelection(true);
        }
      } else {
        console.log('🎯 New user, showing profile selection');
        setShowProfileSelection(true);
      }
    } else if (isAuthenticated && auth0User && user) {
      console.log('👤 User already exists:', user.email);
    } else if (!isAuthenticated && user) {
      console.log('🚪 Not authenticated but app user exists, clearing...');
      setUser(null);
      setShowProfileSelection(false);
    } else if (isAuthenticated && !auth0User) {
      console.log('⚠️ Authenticated but no Auth0 user data yet, waiting...');
    }
  }, [isAuthenticated, auth0User]); // Removed 'user' to prevent circular dependency

  // Clear user when not authenticated
  useEffect(() => {
    if (!isAuthenticated && !isLoading && user) {
      console.log('🔓 Clearing user - not authenticated');
      setUser(null);
    }
  }, [isAuthenticated, isLoading]); // Removed 'user' to prevent circular dependency

  const onNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    console.log('🔔 onNotification called:', message, severity);
    setNotificationMessage(message);
    setShowNotification(true);
    console.log('�� App will re-render due to notification state change');
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
  }, []);

  const handleProfileSwitch = useCallback((newRole: 'customer' | 'agent' | 'admin') => {
    if (user) {
      console.log(`🔄 Switching profile from ${user.role} to ${newRole}`);
      const updatedUser: User = {
        ...user,
        role: newRole
      };
      setUser(updatedUser);
      
      // Save updated profile to localStorage
      localStorage.setItem('procogia_user_profile', JSON.stringify(updatedUser));
    }
  }, [user]);

  const handleProfileSelect = (selectedUser: User) => {
    console.log('🎯 Profile selected:', selectedUser.role);
    setUser(selectedUser);
    setShowProfileSelection(false);
    
    // Save the selected profile
    localStorage.setItem('telecom_demo_user', JSON.stringify(selectedUser));
    
    onNotification(`Profile set to ${selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}!`, 'success');
  };

  // Add manual clear function for debugging
  const handleClearAuth0State = () => {
    console.log('🔧 Manually clearing Auth0 state...');
    clearAuth0State();
    window.location.reload();
  };

  // Protected route wrapper
  const ProtectedRoute = useCallback(({ children }: { children: React.ReactNode }) => {
    if (isLoading) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{ background: 'linear-gradient(135deg, #1fb8cc 0%, #95c100 100%)' }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 3 }} />
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            🎯 ProCogia Telecom AI
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
            Loading secure authentication...
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Please wait...
          </Typography>
        </Box>
      );
    }

    if (!isAuthenticated || !user) {
      return <Navigate to="/" replace />;
    }

    return <>{children}</>;
  }, [isLoading, isAuthenticated, user]);

  // Main app content for authenticated users
  const AppContent: React.FC = () => {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          user={user!}
          systemStatus={systemStatus}
        />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Header
            user={user!}
            onMenuClick={() => setSidebarOpen(!sidebarOpen)}
            onLogout={handleLogout}
            onProfileSwitch={handleProfileSwitch}
            systemStatus={systemStatus}
          />
          <Box component="main" sx={{ flexGrow: 1, p: 3, backgroundColor: '#f5f5f5' }}>
            <Routes>
              <Route path="/dashboard" element={<ProtectedRoute><ModernDashboard user={user!} onNotification={onNotification} /></ProtectedRoute>} />
              <Route path="/chat" element={<ProtectedRoute><SimpleAIChat user={user!} /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><Analytics user={user!} onNotification={onNotification} /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPanel user={user!} systemStatus={systemStatus} onNotification={onNotification} /></ProtectedRoute>} />
              {/* Redirect all other paths to dashboard if authenticated */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={procogiaMuiTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <Router>
          <Routes>
            <Route path="/callback" element={<AuthCallback />} />
            <Route
              path="/"
              element={
                showProfileSelection ? (
                  <ProfileSelection onProfileSelect={handleProfileSelect} />
                ) : isAuthenticated && user ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <div>
                    <LoginPage />
                    {/* Debug button for clearing Auth0 state - remove in production */}
                    {process.env.NODE_ENV === 'development' && (
                      <Box sx={{ position: 'fixed', bottom: 20, right: 20 }}>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={handleClearAuth0State}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          🔧 Clear Auth0 State
                        </Button>
                      </Box>
                    )}
                    {/* Debug info */}
                    {process.env.NODE_ENV === 'development' && (
                      <Box sx={{ position: 'fixed', bottom: 20, left: 20, fontSize: '0.75rem', color: '#666' }}>
                        Loading: {isLoading.toString()}, Auth: {isAuthenticated.toString()}, User: {user ? 'Yes' : 'No'}
                      </Box>
                    )}
                  </div>
                )
              }
            />
            {/* All dashboard routes are handled by AppContent when authenticated */}
            <Route
              path="/*"
              element={
                showProfileSelection ? (
                  <ProfileSelection onProfileSelect={handleProfileSelect} />
                ) : isAuthenticated && user ? (
                  <AppContent />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>

          <Snackbar
            open={showNotification}
            autoHideDuration={3000}
            onClose={() => setShowNotification(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setShowNotification(false)} severity="success">
              {notificationMessage}
            </Alert>
          </Snackbar>

          {authError && (
            <Snackbar
              open={Boolean(authError)}
              autoHideDuration={6000}
              onClose={() => setAuthError(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert onClose={() => setAuthError(null)} severity="error">
                Authentication Error: {authError}
                <br />
                <Button size="small" onClick={handleClearAuth0State} sx={{ mt: 1 }}>
                  🔧 Clear Auth0 State & Retry
                </Button>
              </Alert>
            </Snackbar>
          )}
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App; 